import React from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import { PALETTE, RAG_CLASSES } from './constants';
import { Flag } from './dashboard/DashboardCard';
import { DashboardLoading, fmt, ownerCount, sumCounts } from './dashboard/dashboardFormatting';
import FocusTable from './dashboard/FocusTable';
import HBars from './dashboard/HBars';
import RadioDashboardSkeleton from './dashboard/RadioDashboardSkeleton';
import RadioDonut from './dashboard/RadioDonut';
import { ragBarSegments, ragDonutSegments, ragTotal } from './dashboard/ragHelpers';
import { useGeneralRisk } from './hooks/useGeneralRisk';

// IB-677 rework — Risks dashboard on the shared skeleton. Lead tile = weighted +
// unweighted value; residual RAG profile donut; gross→residual bars; top residual
// focus table + risks-by-type bars.
const RAG_ORDER = ['R', 'A', 'G'];

export default function GeneralRiskDashboardRisks() {
    const { dashboardData, loading } = useGeneralRisk();

    if (loading.dashboard) {
        return <DashboardLoading />;
    }

    const risks = dashboardData?.risks || {};
    const overview = dashboardData?.overview || {};
    const overdue = sumCounts(risks.overdue_actions_count_by_owner);
    const openCount = risks.open_count ?? 0;
    const basisLabel = $t('residual');
    const profileCounts = risks.count_by_controlled_classification;
    const redCount = overview.red_risks;

    const tiles = [
        { lead: true, label: $t('Risk-weighted value'), value: fmt(risks.weighted_value), sub: `${$t('Unweighted value')} ${fmt(risks.unweighted_value)}` },
        {
            label: $t('Open risks'),
            value: openCount,
            sub: `${risks.in_afc_count ?? 0} ${$t('in AFC')} · ${risks.not_in_afc_count ?? 0} ${$t('not in AFC')}`,
        },
        { tone: 'red', label: `${$t('Red')} (${basisLabel})`, value: redCount ?? 0 },
        {
            tone: 'warn',
            label: $t('Overdue actions'),
            value: overdue,
            sub: `${$t('across')} ${ownerCount(risks.overdue_actions_count_by_owner)} ${$t('owners')}`,
        },
        { label: $t('Attached to budget lines'), value: risks.attached_to_budget_lines_count ?? 0, sub: `${$t('of')} ${openCount}` },
    ];

    const profile = {
        title: `${$t('RAG profile')} — ${basisLabel}`,
        content: <RadioDonut segments={ragDonutSegments(profileCounts)} total={`${ragTotal(profileCounts)} ${$t('open')}`} />,
    };

    const shifts = risks.gross_residual_shift?.shifts || {};
    const grossSegments = RAG_ORDER.map((gross, index) => ({
        value: RAG_ORDER.reduce((carry, residual) => carry + (shifts[gross]?.[residual] || 0), 0),
        colourClass: RAG_CLASSES[index],
    }));
    const residualSegments = RAG_ORDER.map((residual, index) => ({
        value: RAG_ORDER.reduce((carry, gross) => carry + (shifts[gross]?.[residual] || 0), 0),
        colourClass: RAG_CLASSES[index],
    }));
    const excluded = risks.gross_residual_shift?.excluded_single_value || 0;

    const keyMetric = {
        title: $t('Gross → residual'),
        flag: <Flag tone="new">{$t('promoted')}</Flag>,
        content: (
            <HBars
                labelWidth={64}
                rows={[
                    { label: $t('Gross'), segments: grossSegments },
                    { label: $t('Residual'), segments: residualSegments },
                ]}
                note={
                    excluded > 0 ? (
                        <>
                            <b className="tw-font-semibold tw-text-charcoal-75">
                                {excluded} {$t('items')}
                            </b>{' '}
                            {$t('excluded — only one of gross / residual recorded. Shown in the register, not in these bars.')}
                        </>
                    ) : null
                }
                emptyLabel={$t('No gross / residual data')}
            />
        ),
    };

    const topResidual = risks.top_residual || [];
    const focus = (
        <FocusTable
            title={$t('Top residual risks')}
            columns={[$t('Risk'), $t('RAG (residual)'), $t('Weighted value')]}
            rows={topResidual.map((risk) => [
                risk.description || risk.id,
                <span
                    key="rag"
                    className="tw-inline-block tw-h-2.5 tw-w-2.5 tw-rounded-full tw-align-middle"
                    style={{ background: risk.residual_classification_color || PALETTE.charcoal25 }}
                />,
                fmt(risk.weighted_value),
            ])}
            empty={$t('No residual risks')}
        />
    );

    const byTypeResidual = risks.by_type_residual || {};
    const extra = {
        title: $t('Risks by type'),
        flag: <Flag tone="new">{$t('new')}</Flag>,
        content: (
            <HBars
                labelWidth={110}
                rows={Object.entries(byTypeResidual).map(([type, counts]) => ({ label: type, segments: ragBarSegments(counts) }))}
                note={$t('Bar length = open risks per type · segments = residual RAG')}
                emptyLabel={$t('No risks by type')}
            />
        ),
    };

    return (
        <RadioDashboardSkeleton
            tiles={tiles}
            profile={profile}
            movement={{ data: overview.movement, category: 'risks' }}
            keyMetric={keyMetric}
            focus={focus}
            extra={extra}
        />
    );
}

if (document.getElementById('generalRiskDashboardRisks')) {
    const root = document.getElementById('generalRiskDashboardRisks');
    ReactDOM.render(
        <App>
            <GeneralRiskDashboardRisks />
        </App>,
        root,
    );
}
